import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Portal,
} from '@headlessui/react';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import {
  Briefcase,
  Building2,
  Clock,
  FileText,
  Search,
  Shield,
  Stethoscope,
  User,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useRecentSearches } from '@/hooks/useRecentSearches';
import {
  EntityType,
  SearchResult,
  useTypeaheadSearchQuery,
} from '@/services/api/searchService';

import { Kbd, KbdGroup } from '../ui/kbd';

const ENTITY_ICONS: Record<EntityType, React.FC<{ className?: string }>> = {
  intake: User,
  matter: Briefcase,
  firm: Building2,
  coverage: Shield,
  carrier: Building2,
  police_department: ShieldCheckIcon,
  provider_contact: Stethoscope,
  police_report: FileText,
};

const ENTITY_LABELS: Record<EntityType, string> = {
  intake: 'Intake',
  matter: 'Matter',
  firm: 'Firm',
  coverage: 'Coverage',
  carrier: 'Carrier',
  police_department: 'Police Department',
  provider_contact: 'Provider Contact',
  police_report: 'Police Report',
};

const formatDateOfLoss = (
  dateStr: string | null | undefined,
): string | null => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return null;
  }
};

const highlightMatch = (text: string, query: string): React.ReactNode => {
  if (!query.trim()) return text;

  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) return text;

  const before = text.slice(0, index);
  const match = text.slice(index, index + query.length);
  const after = text.slice(index + query.length);

  return (
    <>
      <span className="font-normal text-gray-500">{before}</span>
      <span className="font-semibold text-gray-900">{match}</span>
      <span className="font-normal text-gray-500">{after}</span>
    </>
  );
};

const getUrlForResult = (result: SearchResult): string | null => {
  switch (result.entity_type) {
    case 'matter':
      return `/matters/${result.entity_id}`;
    case 'firm':
      return `/firms/${result.entity_id}`;
    case 'carrier':
      return `/carrier-directory?name=${encodeURIComponent(result.label)}`;
    case 'coverage':
      // Check coverage_id first (most specific)
      if ('coverage_id' in result.metadata && 'matter_id' in result.metadata) {
        // Primary path: Direct link to specific coverage detail
        const matterId = result.metadata.matter_id;
        const coverageId = result.metadata.coverage_id;
        return `/matters/${matterId}/coverage/${coverageId}`;
      }
      // Fallback path: Link to matter's coverage list page
      // (Handles future search types or edge cases without coverage_id)
      if ('matter_id' in result.metadata) {
        const matterId = result.metadata.matter_id;
        return `/matters/${matterId}/coverage`;
      }
      // No matter context - cannot navigate
      /* eslint-disable no-console */
      console.warn('Coverage result missing matter_id in metadata', result);
      return null;
    case 'intake':
      return `/intakes/${result.entity_id}`;
    case 'police_department':
      return `/policedepartments/${result.entity_id}`;
    case 'provider_contact':
      return `/provider-directory?provider=${encodeURIComponent(
        result.entity_id,
      )}`;
    case 'police_report':
      // entity_id is matter_id; link to matter (police report context shown inline)
      return `/matters/${result.entity_id}`;
    default:
      /* eslint-disable no-console */
      console.warn('Unknown entity_type in search result', result);
      return null;
  }
};

const SearchResultItem = ({
  result,
  query,
}: {
  result: SearchResult;
  query: string;
}) => {
  const Icon = ENTITY_ICONS[result.entity_type];
  const entityLabel = ENTITY_LABELS[result.entity_type];

  // Build secondary text: Entity type + matched field context
  const getSecondaryText = (): string => {
    const parts = [entityLabel];
    const { metadata } = result;
    const matchedField =
      'matched_field' in metadata ? metadata.matched_field : undefined;

    // Add date of loss for matters and intakes (helps distinguish multiple cases for same client)
    if (result.entity_type === 'matter' || result.entity_type === 'intake') {
      const dateOfLoss =
        'date_of_loss' in metadata ? (metadata.date_of_loss as string) : null;
      const formattedDol = formatDateOfLoss(dateOfLoss);
      if (formattedDol) {
        parts.push(`DOL: ${formattedDol}`);
      }
    }

    // Handle coverage-specific display
    if (result.entity_type === 'coverage') {
      // For coverage, show matter name (client) and firm name
      if (
        matchedField === 'claim_identifier' &&
        'matter_name' in metadata &&
        typeof metadata.matter_name === 'string'
      ) {
        parts.push(metadata.matter_name);
        if ('firm_name' in metadata && typeof metadata.firm_name === 'string') {
          parts.push(metadata.firm_name);
        }
        return parts.join(' • ');
      }

      // For policy identifiers, show matter name and firm name
      if (
        matchedField === 'policy_identifier' &&
        'matter_name' in metadata &&
        typeof metadata.matter_name === 'string'
      ) {
        parts.push(metadata.matter_name);
        if ('firm_name' in metadata && typeof metadata.firm_name === 'string') {
          parts.push(metadata.firm_name);
        }
        return parts.join(' • ');
      }

      // For insured names, show matter name and firm name
      if (
        matchedField === 'named_insured' &&
        'matter_name' in metadata &&
        typeof metadata.matter_name === 'string'
      ) {
        parts.push(metadata.matter_name);
        if ('firm_name' in metadata && typeof metadata.firm_name === 'string') {
          parts.push(metadata.firm_name);
        }
        return parts.join(' • ');
      }
    }

    // Handle police_report - show matter name and firm name for context
    if (result.entity_type === 'police_report') {
      if (
        'matter_name' in metadata &&
        typeof metadata.matter_name === 'string'
      ) {
        parts.push(metadata.matter_name);
      }
      if ('firm_name' in metadata && typeof metadata.firm_name === 'string') {
        parts.push(metadata.firm_name);
      }
      return parts.join(' • ');
    }

    // If we matched on ANY secondary field (not the primary entity/firm name),
    // show the matched details instead of firm name
    if (matchedField) {
      switch (matchedField) {
        case 'client_name':
          if (
            'client_name' in metadata &&
            typeof metadata.client_name === 'string'
          ) {
            parts.push(`Client: ${metadata.client_name}`);
          }
          break;
        case 'phone':
          if ('phone' in metadata && typeof metadata.phone === 'string') {
            parts.push(metadata.phone);
          }
          break;
        case 'email':
          if ('email' in metadata && typeof metadata.email === 'string') {
            parts.push(metadata.email);
          }
          break;
        case 'address':
          if ('address' in metadata && typeof metadata.address === 'string') {
            parts.push(metadata.address);
          }
          break;
        case 'date_of_birth':
          if (
            'date_of_birth' in metadata &&
            typeof metadata.date_of_birth === 'string'
          ) {
            parts.push(`DOB: ${metadata.date_of_birth}`);
          }
          if (
            'client_name' in metadata &&
            typeof metadata.client_name === 'string'
          ) {
            parts.push(`Client: ${metadata.client_name}`);
          }
          break;
        case 'doctor_name':
          if (
            'doctor_name' in metadata &&
            typeof metadata.doctor_name === 'string'
          ) {
            parts.push(`Dr: ${metadata.doctor_name}`);
          }
          if (
            'provider_name' in metadata &&
            typeof metadata.provider_name === 'string'
          ) {
            parts.push(`at ${metadata.provider_name}`);
          }
          break;
        default:
          break;
      }
    }else if (
      'firm_name' in metadata &&
      typeof metadata.firm_name === 'string'
    ) {
      // No matched_field means we matched on the primary entity name
      // Show firm name for context (except for firm entities themselves)
      parts.push(metadata.firm_name);
    }

    return parts.join(' • ');
  };

  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="h-5 w-5 flex-shrink-0 mt-0.5 text-gray-600" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">
          {highlightMatch(result.label, query)}
        </div>
        <div className="text-xs text-gray-400 mt-0.5 truncate">
          {getSecondaryText()}
        </div>
      </div>
    </div>
  );
};

export const TypeaheadSearch = () => {
  const [inputValue, setInputValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [displayResults, setDisplayResults] = useState<SearchResult[]>([]);
  const [lastProcessedQuery, setLastProcessedQuery] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const navigate = useNavigate();
  const { recentSearches, saveRecentSearch } = useRecentSearches();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(inputValue);
    }, 150);

    return () => clearTimeout(timer);
  }, [inputValue]);

  // Keyboard shortcut to toggle focus search (Cmd+K or Ctrl+K) and ESC to dismiss
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (document.activeElement === inputRef.current) {
          inputRef.current?.blur();
        } else {
          inputRef.current?.focus();
        }
      }
      // ESC key dismisses the search bar when focused
      if (e.key === 'Escape' && document.activeElement === inputRef.current) {
        inputRef.current?.blur();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Fetch whenever there's input (no minimum character requirement)
  const shouldFetch = debouncedQuery.length > 0;
  const { data, isLoading, isError } = useTypeaheadSearchQuery(debouncedQuery, {
    skip: !shouldFetch,
  });

  // Only update displayed results when the completed query matches current input
  useEffect(() => {
    if (inputValue === '') {
      // Input cleared - clear results immediately
      setDisplayResults([]);
      setLastProcessedQuery('');
    } else if (debouncedQuery === inputValue && !isLoading) {
      // Query completed (success or error) and matches current input

      // CRITICAL: Verify data actually corresponds to current query
      // RTK Query can have stale data from previous query in the cache
      if (data?._query !== debouncedQuery) {
        return;
      }

      // CRITICAL: Prevent processing the same query twice or out-of-order responses
      if (lastProcessedQuery === debouncedQuery && data?.results) {
        return;
      }

      if (isError) {
        // API error - clear results to avoid showing stale data
        setDisplayResults([]);
        setLastProcessedQuery(debouncedQuery);
      } else if (data?.results !== undefined) {
        // Success - update results (even if empty array)
        setDisplayResults(data.results);
        setLastProcessedQuery(debouncedQuery);
      }
    }
    // During typing (debouncedQuery !== inputValue), keep previous results
  }, [
    debouncedQuery,
    inputValue,
    data,
    isLoading,
    isError,
    lastProcessedQuery,
  ]);

  // Show dropdown only when focused AND (there's a search query OR recent searches available)
  const showDropdown =
    isFocused && (inputValue.length > 0 || recentSearches.length > 0);

  const handleClear = () => {
    setInputValue('');
    setDebouncedQuery('');
    setDisplayResults([]);
    setLastProcessedQuery('');
  };

  // Handle selection (both click and keyboard Enter)
  const handleSelect = (result: SearchResult | null) => {
    if (!result) return;

    const url = getUrlForResult(result);
    if (!url) {
      /* eslint-disable no-console */
      console.error('Cannot navigate: invalid URL for result', result);
      return;
    }

    // Save to recent searches
    saveRecentSearch(result);

    navigate(url);
    setInputValue('');
    setDebouncedQuery('');
    setDisplayResults([]);
    setLastProcessedQuery('');
    setIsFocused(false);
  };

  return (
    <Combobox value={null} onChange={handleSelect}>
      <div className="relative w-full">
        <div
          className="relative flex items-center"
          title="Press ⌘K to focus search"
        >
          <Search className="absolute left-3 h-4 w-4 text-gray-400 pointer-events-none" />
          <ComboboxInput
            ref={inputRef}
            className={`w-full rounded-full bg-white/20 py-2 pl-10 pr-10 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white transition-all duration-100 ease-in-out ${
              !isFocused && inputValue ? 'text-gray-400' : ''
            }`}
            placeholder="Search all of Finch..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
          {!inputValue && !isFocused && (
            <div className="absolute right-3 flex items-center pointer-events-none">
              <KbdGroup>
                <Kbd className="bg-white/20 text-white/70 ">⌘K</Kbd>
              </KbdGroup>
            </div>
          )}
          {inputValue && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-3 h-4 w-4 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {showDropdown && (
          <Portal>
            <ComboboxOptions
              anchor="bottom start"
              className="z-[9999] mt-2 w-80 rounded-lg border border-gray-200 bg-white shadow-lg"
              static
            >
            {/* Recent searches - show when input is empty */}
            {inputValue.length === 0 && recentSearches.length > 0 && (
              <>
                <div className="px-4 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100">
                  Recent Searches
                </div>
                {recentSearches.map((result) => {
                  // Build secondary text for recent searches
                  const parts = [ENTITY_LABELS[result.entity_type]];

                  // Add date of loss for matters and intakes
                  if (
                    result.entity_type === 'matter' ||
                    result.entity_type === 'intake'
                  ) {
                    const dateOfLoss =
                      'date_of_loss' in result.metadata
                        ? (result.metadata.date_of_loss as string)
                        : null;
                    const formattedDol = formatDateOfLoss(dateOfLoss);
                    if (formattedDol) {
                      parts.push(`DOL: ${formattedDol}`);
                    }
                  }

                  // Special handling for coverage and police_report - show matter name before firm name
                  if (
                    result.entity_type === 'coverage' ||
                    result.entity_type === 'police_report'
                  ) {
                    if (
                      'matter_name' in result.metadata &&
                      typeof result.metadata.matter_name === 'string'
                    ) {
                      parts.push(result.metadata.matter_name);
                    }
                  }

                  // Add firm name if present (for all entity types)
                  if (
                    'firm_name' in result.metadata &&
                    typeof result.metadata.firm_name === 'string'
                  ) {
                    parts.push(result.metadata.firm_name);
                  }

                  return (
                    <ComboboxOption
                      key={`recent-${result.entity_type}-${result.entity_id}`}
                      value={result}
                      className="cursor-pointer mx-2 my-1 rounded-lg px-3 data-[focus]:bg-[#E9E5FF]"
                    >
                      <div className="flex items-start gap-3 py-2">
                        <Clock className="h-5 w-5 flex-shrink-0 mt-0.5 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {result.label}
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5 truncate">
                            {parts.join(' • ')}
                          </div>
                        </div>
                      </div>
                    </ComboboxOption>
                  );
                })}
              </>
            )}

            {/* Loading state - only during actual API calls */}
            {isLoading && (
              <div className="px-4 py-6 text-center text-sm text-gray-500">
                Searching...
              </div>
            )}

            {/* Error state */}
            {!isLoading && isError && debouncedQuery.length > 0 && (
              <div className="px-4 py-6 text-center text-sm text-gray-500">
                Search temporarily unavailable. Try refreshing the page.
              </div>
            )}

            {/* No results - only show after a search completes with no results */}
            {!isLoading &&
              !isError &&
              debouncedQuery.length > 0 &&
              displayResults.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-gray-500">
                  No results found
                </div>
              )}

            {/* Results list */}
            {displayResults.length > 0 && !isLoading && (
              <>
                {displayResults.map((result) => (
                  <ComboboxOption
                    key={`${result.entity_type}-${result.entity_id}`}
                    value={result}
                    className="cursor-pointer mx-2 my-1 rounded-lg px-3 data-[focus]:bg-[#E9E5FF]"
                  >
                    <SearchResultItem result={result} query={debouncedQuery} />
                  </ComboboxOption>
                ))}
              </>
            )}
            </ComboboxOptions>
          </Portal>
        )}
      </div>
    </Combobox>
  );
};
